import React, { lazy, Suspense, memo, useMemo, useCallback, useEffect } from 'react';
import { withSitecoreContext } from '@sitecore-jss/sitecore-jss-react';
import NewHero from './NewHero/NewHero'; // 首屏立即加载

// 懒加载非首屏组件
const Features = lazy(() => import('./Features/Features'));
const Video = lazy(() => import('./Video/Video'));
const News = lazy(() => import('./News/News'));
const ParallaxCard = lazy(() => import('./ParallaxCard/ParallaxCard'));
const InteractiveBooking = lazy(() => import('./InteractiveBooking/InteractiveBooking'));
const TermsAndConditions = lazy(() => import('./TermsAndConditionsSection/TermsAndConditions'));

// 加载占位符
const LoadingFallback = () => (
  <div
    style={{
      minHeight: '400px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: '#f5f5f5',
      margin: '20px 0',
    }}
  >
    <div
      style={{
        padding: '20px',
        borderRadius: '8px',
        backgroundColor: 'white',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        color: '#666',
      }}
    ></div>
  </div>
);

const HomePage = memo(function HomePage(props) {
  const { fields } = props;

  // 使用 useMemo 缓存计算结果，避免重复计算
  const processedFields = useMemo(() => {
    const {
      hero,
      carousel,
      video,
      videoTitle,
      videoText,
      aboutTitle,
      aboutText,
      aboutImage,
      aboutButtonText,
      aboutButtonUrl,
      contactButtonText,
      contactButtonUrl,
      comingSoonText,
      newsList,
      interactive,
      termsTitle,
      termsText,
      readText,
      newsButtonDefault,
      newsButtonOpposite,
    } = fields || {};

    // 创建 textBlock 对象
    const textBlock = {
      title: videoTitle?.value || videoTitle,
      text: videoText?.value || videoText,
    };

    // 创建 aboutBlock 对象
    const aboutBlock = {
      title: aboutTitle,
      text: aboutText,
      image: aboutImage,
      aboutButtonText: aboutButtonText?.value || aboutButtonText,
      aboutButtonUrl: aboutButtonUrl?.value || aboutButtonUrl,
      contactButtonText: contactButtonText?.value || contactButtonText,
      contactButtonUrl: contactButtonUrl?.value || contactButtonUrl,
    };

    // 创建按钮配置对象
    const buttonConfig = {
      comingSoonText: comingSoonText?.value || comingSoonText,
      defaultHeroText: 'Welcome to Jaecoo', // 可以后续从Sitecore获取
      defaultHeroImage: '/public/images/car-sunset-road.png', // 可以后续从Sitecore获取
    };

    return {
      hero,
      carousel,
      video,
      textBlock,
      aboutBlock,
      buttonConfig,
      newsList,
      interactive,
      termsTitle,
      termsText,
      readText,
      newsButtonDefault,
      newsButtonOpposite,
    };
  }, [fields]);

  const {
    hero,
    carousel,
    video,
    textBlock,
    aboutBlock,
    buttonConfig,
    newsList,
    interactive,
    termsTitle,
    termsText,
    readText,
    newsButtonDefault,
    newsButtonOpposite,
  } = processedFields;

  // 添加调试信息

  // 在组件挂载后刷新AOS（处理懒加载组件）
  useEffect(() => {
    // 延迟刷新AOS，确保所有懒加载组件都渲染完成
    const refreshTimer = setTimeout(() => {
      if (window.AOS) {
        window.AOS.refresh();
        console.log('🔄 HomePage: AOS刷新完成');
      }
    }, 3000); // 3秒后刷新，确保所有懒加载组件都已加载

    return () => clearTimeout(refreshTimer);
  }, []);

  return (
    <>
      {/* 首屏 - 立即渲染 */}
      <NewHero hero={hero || []} buttonConfig={buttonConfig} />

      {/* 非首屏 - 懒加载 */}
      <Suspense fallback={<LoadingFallback />}>
        <Features carousel={carousel || []} buttonConfig={buttonConfig} />
      </Suspense>

      <Suspense fallback={<LoadingFallback />}>
        <Video video={video} textBlock={textBlock} />
      </Suspense>

      <Suspense fallback={<LoadingFallback />}>
        <News
          newsList={newsList}
          readText={readText}
          newsButtonDefault={newsButtonDefault}
          newsButtonOpposite={newsButtonOpposite}
        />
      </Suspense>

      <Suspense fallback={<LoadingFallback />}>
        <ParallaxCard aboutBlock={aboutBlock} />
      </Suspense>

      <Suspense fallback={<LoadingFallback />}>
        <InteractiveBooking interactive={interactive} />
      </Suspense>

      {/* <TermsAndConditions termsTitle={termsTitle} termsText={termsText} /> */}
    </>
  );
});

// 使用 useCallback 优化回调函数
const MemoizedHomePage = memo(HomePage);

export default withSitecoreContext()(MemoizedHomePage);
